import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CircleDollarSign, Receipt, AlertTriangle, Wallet, Activity } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "#94a3b8",
  sent: "#3b82f6",
  paid: "#22c55e",
  overdue: "#ef4444",
  cancelled: "#cbd5e1",
};

const ACTIVITY_ICONS = {
  created: Receipt,
  sent: Receipt,
  paid: CircleDollarSign,
  cancelled: AlertTriangle,
};

function KpiCard({ label, value, currency, tone = "default", icon: Icon, testId }) {
  const tones = {
    default: "bg-white",
    good: "bg-emerald-50",
    warn: "bg-amber-50",
    bad: "bg-red-50",
  };
  const iconTones = {
    default: "text-slate-500",
    good: "text-emerald-600",
    warn: "text-amber-600",
    bad: "text-red-600",
  };
  return (
    <Card
      data-testid={testId}
      className={`${tones[tone]} border-slate-200 shadow-none`}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
          {Icon && <Icon className={`h-4 w-4 ${iconTones[tone]}`} />}
        </div>
        <div className="mt-2 font-heading text-2xl tabular-nums text-slate-900">
          {value}
        </div>
        {currency && (
          <div className="text-xs text-slate-500">{currency}</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user, company } = useAuth();
  const lang = i18n.language?.split("-")[0] || "en";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ccy, setCcy] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/company/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/dashboard/summary")
      .then(({ data }) => {
        if (!mounted) return;
        setData(data);
        const defaultCcy =
          data.currencies?.find((c) => c.currency === company?.default_currency)?.currency ||
          data.currencies?.[0]?.currency;
        setCcy(defaultCcy);
      })
      .catch((e) => toast.error(formatApiError(e, "Failed to load dashboard")))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [company]);

  const active = useMemo(
    () => data?.currencies?.find((c) => c.currency === ccy) || null,
    [data, ccy]
  );

  const pieData = useMemo(() => {
    if (!active) return [];
    return Object.entries(active.invoice_count_by_status)
      .filter(([, n]) => n > 0)
      .map(([k, v]) => ({ status: k, count: v }));
  }, [active]);

  const monthLabels = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(2020, i, 1);
      return new Intl.DateTimeFormat(lang, { month: "short" }).format(d);
    });
  }, [lang]);

  const barData = useMemo(() => {
    if (!active) return [];
    return active.monthly.map((m, i) => ({
      month: monthLabels[i],
      invoiced: m.invoiced,
      paid: m.paid,
    }));
  }, [active, monthLabels]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-500" data-testid="dashboard-loading">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {company?.name}
          </div>
          <h1 className="font-heading text-4xl tracking-tight text-slate-900">
            {t("dashboard.welcome", { name: user?.name?.split(" ")[0] || user?.name })}
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-slate-500">
            {t("dashboard.subtitle")}
          </p>
        </div>
        {data?.currencies?.length > 1 && (
          <div className="w-40">
            <Select value={ccy} onValueChange={setCcy}>
              <SelectTrigger data-testid="dashboard-ccy-picker">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {data.currencies.map((c) => (
                  <SelectItem key={c.currency} value={c.currency}>
                    {c.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </header>

      {usage?.plan === "free" && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="dashboard-usage-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-800">{t("quota.freePlanBadge")}</div>
              <div className="mt-1 text-sm text-amber-900">
                {t("quota.invoicesUsed", { used: usage.invoices_this_week, cap: usage.limits?.invoices_per_week ?? 10 })}
              </div>
            </div>
            <a
              href="mailto:info@nordicscandiskroyal.se?subject=InvoicePro%20Upgrade"
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              data-testid="dashboard-upgrade-cta"
            >
              {t("quota.upgradeCta")}
            </a>
          </div>
        </div>
      )}

      {active && (
        <>
          <section className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="dashboard-kpis">
            <KpiCard
              label={t("dashboard.kpi.invoiced")}
              value={formatCurrency(active.total_invoiced, active.currency, lang)}
              currency={active.currency}
              icon={Receipt}
              testId="kpi-invoiced"
            />
            <KpiCard
              label={t("dashboard.kpi.paid")}
              value={formatCurrency(active.total_paid, active.currency, lang)}
              tone="good"
              currency={active.currency}
              icon={CircleDollarSign}
              testId="kpi-paid"
            />
            <KpiCard
              label={t("dashboard.kpi.outstanding")}
              value={formatCurrency(active.outstanding, active.currency, lang)}
              tone="warn"
              currency={active.currency}
              icon={Wallet}
              testId="kpi-outstanding"
            />
            <KpiCard
              label={t("dashboard.kpi.overdue")}
              value={formatCurrency(active.overdue, active.currency, lang)}
              tone="bad"
              currency={active.currency}
              icon={AlertTriangle}
              testId="kpi-overdue"
            />
          </section>

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="col-span-1 lg:col-span-2 border-slate-200 shadow-none">
              <CardContent className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-lg text-slate-900">
                    {t("dashboard.chart.monthly")}
                  </h2>
                  <div className="text-xs text-slate-500">{active.currency}</div>
                </div>
                <div className="h-72" data-testid="dashboard-chart-monthly">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#64748b" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="invoiced" fill="#0f172a" name={t("dashboard.kpi.invoiced")} />
                      <Bar dataKey="paid" fill="#22c55e" name={t("dashboard.kpi.paid")} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-5">
                <h2 className="mb-4 font-heading text-lg text-slate-900">
                  {t("dashboard.chart.status")}
                </h2>
                <div className="h-72" data-testid="dashboard-chart-status">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={80}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.status] || "#94a3b8"} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-5">
                <h2 className="mb-4 font-heading text-lg text-slate-900">
                  {t("dashboard.top.title")}
                </h2>
                {active.top_customers.length === 0 ? (
                  <div className="text-sm text-slate-500">{t("dashboard.top.empty")}</div>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="dashboard-top-customers">
                    {active.top_customers.map((c, i) => (
                      <li key={i} className="flex items-center justify-between py-2 text-sm">
                        <span className="truncate text-slate-900">{c.name}</span>
                        <span className="tabular-nums text-slate-600">
                          {formatCurrency(c.amount, active.currency, lang)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-5">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg text-slate-900">
                  <Activity className="h-4 w-4 text-slate-500" />
                  {t("dashboard.recent.title")}
                </h2>
                {(!data.recent_activity || data.recent_activity.length === 0) ? (
                  <div className="text-sm text-slate-500">{t("dashboard.recent.empty")}</div>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="dashboard-recent">
                    {data.recent_activity.map((a, i) => {
                      const Icon = ACTIVITY_ICONS[a.type] || Receipt;
                      return (
                        <li key={i} className="flex items-center gap-3 py-2 text-sm">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
                            <Icon className="h-4 w-4 text-slate-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <NavLink
                              to={`/app/invoices/${a.invoice_id}`}
                              className="truncate font-medium text-slate-900 hover:underline"
                            >
                              {a.invoice_number}
                            </NavLink>
                            <div className="truncate text-xs text-slate-500">
                              {t(`dashboard.recent.${a.type}`)} · {a.customer_name}
                            </div>
                          </div>
                          <div className="text-end text-xs">
                            <div className="tabular-nums text-slate-900">
                              {formatCurrency(a.amount, a.currency, lang)}
                            </div>
                            <div className="text-slate-400">
                              {formatDate((a.at || "").split("T")[0], lang)}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}
